import React, { FunctionComponent, useEffect, useRef } from "react";
import {
  InterpretationLayout,
  FeedScroll,
  MessageCard,
  CardHeader,
  SpeakerBadge,
  CardTimestamp,
  PrimaryText,
  SecondaryDivider,
  SecondaryRow,
  LangBadge,
  SecondaryText,
  StreamingCursor,
  EmptyState
} from "./interpretationStyles";
import { CaptionFade } from "./transcribeStyles";
import { TranscriptStore, filterCards, isEnglishLang, useTranscriptStream } from "./stream";
import { createUserScrollTracker, isScrolledAboveBottom, scrollElementToBottom } from "./autoScroll";

interface IInterpretationViewProps {
  /** The single source of truth for transcription + interpretation state. */
  store: TranscriptStore;
  search: string;
  getTimestamp: (value: any) => string;
  correlationId?: string;
  autoScroll?: boolean;
  onAutoScrollChange?: (enabled: boolean) => void;
}

/**
 * Presentational live-call feed. It holds NO transcription business logic: the
 * `useTranscriptStream` hook turns the deterministic store state into
 * render-ready view models (with character-streamed text), and this component
 * simply maps them to cards. One utterance == one card with a stable key.
 */
export const InterpretationView: FunctionComponent<IInterpretationViewProps> = ({
  store,
  search,
  getTimestamp,
  correlationId,
  autoScroll = true,
  onAutoScrollChange
}) => {
  const views = useTranscriptStream(store);
  const items = filterCards(views, search);

  const feedRef = useRef<HTMLDivElement | null>(null);
  const lastTailIdRef = useRef<string | null>(null);
  const prevAutoScrollRef = useRef(autoScroll);
  const userScrollRef = useRef(createUserScrollTracker());

  const scrollId = correlationId
    ? `transcription-container-${correlationId}`
    : "transcription-container-interpretation";

  const lastItem = items[items.length - 1];
  const tailKey =
    items.length === 0
      ? "empty"
      : `${items.length}:${lastItem?.id}:${lastItem?.english.length}:${lastItem?.original.length}`;

  useEffect(() => {
    return () => userScrollRef.current.dispose();
  }, []);

  useEffect(() => {
    if (!autoScroll) {
      prevAutoScrollRef.current = false;
      return;
    }
    const el =
      feedRef.current ||
      (typeof document !== "undefined" ? document.getElementById(scrollId) : null);
    if (!el) return;

    const justEnabled = !prevAutoScrollRef.current;
    prevAutoScrollRef.current = true;
    if (justEnabled) {
      scrollElementToBottom(el);
      return;
    }

    const tailId = lastItem ? String(lastItem.id) : null;
    const isNewCard = tailId !== lastTailIdRef.current;
    lastTailIdRef.current = tailId;

    if (isNewCard) {
      scrollElementToBottom(el);
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 140) {
      el.scrollTop = el.scrollHeight;
    }
  }, [tailKey, autoScroll, scrollId, lastItem]);

  const handleUserScrollIntent = () => {
    userScrollRef.current.mark();
  };

  const handleScroll = () => {
    if (!autoScroll || !userScrollRef.current.isUserScrolling()) return;
    const el = feedRef.current;
    if (!el) return;
    if (isScrolledAboveBottom(el)) {
      onAutoScrollChange?.(false);
    }
  };

  return (
    <InterpretationLayout className="interpretation-view">
      <FeedScroll
        ref={feedRef}
        id={scrollId}
        className="interpretation-feed"
        onWheel={handleUserScrollIntent}
        onTouchStart={handleUserScrollIntent}
        onPointerDown={handleUserScrollIntent}
        onScroll={handleScroll}
      >
        {items.length === 0 ? (
          <EmptyState>No transcription yet.</EmptyState>
        ) : (
          items.map((item) => (
            <MessageCard
              key={item.id}
              accent={item.accent}
              stripe={item.stripe}
              className={`interpretation-card interpretation-card-${item.party}`}
            >
              <CardHeader>
                <SpeakerBadge accent={item.accent}>
                  {item.party === "interpreter" && item.aiInterpreter && (
                    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <path
                        fill="currentColor"
                        d="M8 1.5l1.76 3.57 3.94.57-2.85 2.78.67 3.92L8 10.77l-3.52 1.85.67-3.92L2.3 5.64l3.94-.57L8 1.5z"
                      />
                    </svg>
                  )}
                  {item.label}
                </SpeakerBadge>
                {item.createdAt ? (
                  <CardTimestamp>{getTimestamp(item.createdAt)}</CardTimestamp>
                ) : null}
              </CardHeader>

              {/*
                Field ownership: a card is one speaker + one spoken language +
                one or more consecutive utterances. Its interpretation and rolling
                transcription each render as their own section (`english` = the
                interpretation, `original` = the rolling transcription — never
                mixed). `toCards` concatenates the speaker's utterances in speech
                order, inserting a line break at each completed-sentence boundary
                (an ASR-split continuation stays on the same line). PrimaryText /
                SecondaryText use `white-space: pre-wrap`, so those newlines
                render as line breaks within the current speaker card.
              */}
              {/*
                Agent leg is deemphasized so the agent's focus stays on the
                caller. It is additionally italicized only while that leg is
                transcribed as English — the agent speaking for themselves. A
                human interpreter relaying on the same leg arrives in the
                caller's language, so that turn stays upright and the two are
                distinguishable at a glance.
              */}
              {item.english ? (
                <PrimaryText
                  muted={item.party === "agent"}
                  emphasized={item.party === "agent" && isEnglishLang(item.sourceLang)}
                  className={`interpretation-primary${item.party === "agent" ? " is-agent" : ""}${
                    item.party === "agent" && isEnglishLang(item.sourceLang) ? " is-agent-english" : ""
                  }`}
                >
                  <CaptionFade>
                    {item.english}
                    {item.englishStreaming ? <StreamingCursor aria-hidden="true" /> : null}
                  </CaptionFade>
                </PrimaryText>
              ) : null}

              {item.original ? (
                <>
                  {item.english ? <SecondaryDivider /> : null}
                  <SecondaryRow>
                    {item.lang ? <LangBadge>{item.lang}</LangBadge> : null}
                    <SecondaryText slant={item.party !== "agent"}>
                      <CaptionFade>
                        <span>{item.original}</span>
                        {item.originalStreaming ? <StreamingCursor aria-hidden="true" /> : null}
                      </CaptionFade>
                    </SecondaryText>
                  </SecondaryRow>
                </>
              ) : null}
            </MessageCard>
          ))
        )}
      </FeedScroll>
    </InterpretationLayout>
  );
};

export default InterpretationView;
